import { formatStreetAddress } from '../utils/propertyAddress';
const DISCOUNT_LIST_URL = 'https://tophamtechbe.com/api/treas/media/private/v1/discount-list';
const API_KEY = 'treas-api-key-2025';

export const DISCOUNT_LIST_COLUMNS = [
  'Address',
  'City',
  'State',
  'Zip',
  'County',
  'List Price',
  'Days on Market'
];

export const DISCOUNT_LIST_HEADER = DISCOUNT_LIST_COLUMNS.join('\t');

const getNonBlankLines = (text = '') => (
  text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((line) => line.trim() !== '')
);

export const countDiscountListRows = (text) => Math.max(0, getNonBlankLines(text).length - 1);

export const normalizeDiscountListText = (text) => getNonBlankLines(text)
  .map((line, index) => (index === 0 ? line.replace(/^\uFEFF/, '') : line))
  .join('\n');

export const validateDiscountListText = (text) => {
  const lines = getNonBlankLines(text);
  if (!lines.length) {
    return { valid: false, error: 'Paste the tab-separated header and at least one property.' };
  }

  const submittedColumns = lines[0]
    .replace(/^\uFEFF/, '')
    .split('\t')
    .map((column) => column.trim().toLowerCase());
  const requiredColumns = DISCOUNT_LIST_COLUMNS.map((column) => column.toLowerCase());

  if (
    submittedColumns.length !== requiredColumns.length
    || submittedColumns.some((column, index) => column !== requiredColumns[index])
  ) {
    return { valid: false, error: 'The first row must contain the seven required tab-separated column names in order.' };
  }

  const propertyLines = lines.slice(1);
  if (!propertyLines.length) {
    return { valid: false, error: 'Add at least one property below the header row.' };
  }
  if (propertyLines.length > 100) {
    return { valid: false, error: 'The list can contain no more than 100 properties.' };
  }

  for (let index = 0; index < propertyLines.length; index += 1) {
    const rowNumber = index + 2;
    const values = propertyLines[index].split('\t').map((value) => value.trim());
    if (values.length !== DISCOUNT_LIST_COLUMNS.length) {
      return { valid: false, error: `Row ${rowNumber} must contain exactly seven tab-separated columns.` };
    }
    if (values.slice(0, 4).some((value) => !value)) {
      return { valid: false, error: `Row ${rowNumber} requires Address, City, State, and Zip.` };
    }
    if (!/^[A-Za-z]{2}$/.test(values[2])) {
      return { valid: false, error: `Row ${rowNumber} must use a two-letter state abbreviation.` };
    }
    if (values[6] && !/^\d+$/.test(values[6])) {
      return { valid: false, error: `Row ${rowNumber} Days on Market must be a whole number or blank.` };
    }
  }

  return { valid: true, rowCount: propertyLines.length };
};

export const submitDiscountList = async ({ siteId, text }) => {
  const response = await fetch(`${DISCOUNT_LIST_URL}/${encodeURIComponent(siteId)}`, {
    method: 'POST',
    headers: {
      'X-API-Key': API_KEY,
      'Content-Type': 'text/plain; charset=utf-8'
    },
    body: normalizeDiscountListText(text)
  });

  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(result.error || 'Unable to update the eligible property list.');
  }

  return result;
};

export const getDiscountList = async ({ siteId, signal } = {}) => {
  const response = await fetch(`${DISCOUNT_LIST_URL}/${encodeURIComponent(siteId)}`, {
    headers: {
      'X-API-Key': API_KEY,
      'Accept': 'application/json'
    },
    signal
  });

  if (response.status === 404) return null;

  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(result.error || 'Unable to retrieve the eligible property list.');
  }

  return result;
};

const getBathroomTotal = (property = {}) => {
  const reported = Number(property.bathrooms_total_integer ?? property.bathrooms_total);
  if (Number.isFinite(reported) && reported > 0) return reported;

  const full = Number(property.bathrooms_full || 0);
  const half = Number(property.bathrooms_half || 0);
  const calculated = full + (half * 0.5);
  return calculated || null;
};

export const getDisplayableDiscountProperties = (document) => {
  const properties = (document?.properties || [])
    .filter((entry) => entry?.match_status === 'matched')
    .map((entry) => {
      const property = entry.property || {};
      const source = entry.source || {};
      const location = property.location || {};
      const listingKey = entry.listing_key || property.listing_key;
      const media = [...(entry.media || [])]
        .sort((first, second) => Number(first.order_hint || 0) - Number(second.order_hint || 0));
      const imageUrl = media.find((item) => item?.url)?.url || null;
      const street = formatStreetAddress(location);
      const address = entry.requested_address || [
        street,
        location.city || source.city,
        location.state || source.state,
        location.postal_code || source.zip
      ].filter(Boolean).join(', ');

      return {
        listingKey,
        propertyId: entry.property_id || property.id,
        address,
        city: source.city || location.city || property.city,
        price: property.current_price ?? property.list_price ?? source.list_price ?? null,
        bedrooms: property.bedrooms_total ?? null,
        bathrooms: getBathroomTotal(property),
        daysOnMarket: source.days_on_market ?? null,
        imageUrl
      };
    })
    .filter((property) => property.listingKey && property.address);

  return Array.from(
    new Map(properties.map((property) => [property.listingKey, property])).values()
  );
};
